import React, { useState, useRef, useEffect,useCallback, useMemo,useLayoutEffect } from 'react';
import { Table, Input, Button, Space, Row, Col, Card, Breadcrumb,Empty } from 'antd';
import { PlusOutlined, HomeOutlined, SearchOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import { Resizable } from 'react-resizable'; 
import Create from './Create';
import { useOutletContext } from 'react-router-dom';
import GroupAPI from '../../../restAPI/GroupAPI';
import { useSelector } from 'react-redux';
import Edit from './Edit';
import View from './View';

const ResizableTitle = (props) => {
  const { onResize, width, children, ...restProps } = props;
  if (!width) return <th {...restProps}>{children}</th>;

  return (
    <th {...restProps}>
      <Resizable
        width={width}
        height={0}
        onResize={onResize}
        draggableOpts={{ enableUserSelectHack: false }}
        handle={
          <span
            style={{
              position: "absolute",
              right: 0,
              bottom: 0,
              cursor: "col-resize",
              width: "2px",
              height: "100%",
              background: "#ffeb3b3b",
            }}
          />
        }
      >
        <div>{children}</div>
      </Resizable>
    </th>
  );
};

export default function GroupTable() {
  const tableContainerRef = useRef();
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef();
  const createRef = useRef(); // Create ref for the Create component
  const editRef = useRef();
  const viewRef = useRef();
  const { token } = useSelector((state) => state.auth);
  const [allGroupsForDisplay, setAllGroupsForDisplay] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const { groupId } = useOutletContext();

  // Open the Drawer in the Create component
  const showChildDrawer = () => {
    if (createRef.current) {
      createRef.current.openDrawer(); // Call the openDrawer method in Create
    }
  };

  useEffect(() => {
    getGroupsForLoggedUser();
  }, [groupId]);

  const getGroupsForLoggedUser = async () => {
    await GroupAPI.getGroupsForLoggedUser(
      token,
      localStorage.getItem("groupId"),
      localStorage.getItem("companyId")
    )
      .then((res) => {
        if (res?.data) {
          console.log("Group data retrival");
          console.log(JSON.stringify(res.data));
          const rows = [];
          res.data.map((rowValue) => {
            rows.push({
              _id: rowValue._id,
              groupName: rowValue.name,
              country: rowValue.country.name.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
              email: rowValue.email,
              status: rowValue.status,
            });
          });
          setAllGroupsForDisplay(rows);
          setAllGroups(res.data);
        }
      })
      .catch((err) => {
        console.log("error getting contry codes data :", err.message);
      });
  };

  // Refresh the table
  const refreshTable = () => {
    // Placeholder for actual data refresh logic
    getGroupsForLoggedUser();
  };

  // Column search functionality
  const getColumnSearchProps = (dataIndex,placeholder) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${placeholder}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    setSearchedColumn("");
    confirm();
  };

  const onEditChange = (id) => {
    if (editRef.current) {
      editRef.id = id;
      editRef.current.openEditDrawer(); // Call the openDrawer method in Edit
    }
  };

  const onViewChange = (id) => {
    if (viewRef.current) {
      viewRef.id = id;
      viewRef.current.openViewDrawer(); // Call the openDrawer method in Edit
    }
  };

  const initialColumns = [
    {
      title: "Group Name",
      dataIndex: "groupName",
      key: "groupName",
      ...getColumnSearchProps("groupName","Group Name"),
      percentageWidth: 0.30,
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      sorter: (a, b) => a.country.localeCompare(b.country),
      percentageWidth: 0.20,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      onFilter: (value, record) => record.status.indexOf(value) === 0,
      percentageWidth: 0.30,
    },
    {
      title: "Action",
      key: "action",
      percentageWidth: 0.10,
      align:"center",
      render: (_, record) => (
        <Space
          size="small"
          style={{ display: "flex", justifyContent: "space-around" }}
        >
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={(e) => onViewChange(record._id)}
          />
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={(e) => onEditChange(record._id)}
          />
        </Space>
      ),
    },
  ];

    // Compute an initial container width using window.innerWidth as fallback.
    const initialContainerWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;

    // Set initial column widths based on the fallback container width.
    const [columns, setColumns] = useState(
      initialColumns.map(col => ({
        ...col,
        width: Math.floor(initialContainerWidth * col.percentageWidth),
      }))
    );
  
    // Function to update column widths based on the measured container.
    const updateColumnWidths = useCallback(() => {
      if (tableContainerRef.current) {
        const containerWidth = tableContainerRef.current.offsetWidth;
        setColumns(initialColumns.map(col => ({
          ...col,
          width: Math.floor(containerWidth * col.percentageWidth),
        })));
      }
    }, [initialColumns]);
  
    // Use useLayoutEffect to update column widths as soon as the component is mounted.
    useLayoutEffect(() => {
      updateColumnWidths();
    }, [updateColumnWidths]);
  
    // Update widths on window resize.
    useEffect(() => {
      window.addEventListener('resize', updateColumnWidths);
      return () => window.removeEventListener('resize', updateColumnWidths);
    }, [updateColumnWidths]);
  
  
  const handleResize = useCallback(
      (index) => (e, { size }) => {
        setColumns((prevColumns) => {
          const newColumns = [...prevColumns];
          newColumns[index] = { ...newColumns[index], width: size.width };
          return newColumns;
        });
      },
      []
    );
    
    const mergedColumns = useMemo(
      () =>
        columns.map((col, index) => ({
          ...col,
          onHeaderCell: (column) => ({
            width: column.width,
            onResize: handleResize(index),
          }),
        })),
      [columns, handleResize]
    );

  return (
    <Row>
      <Col md={24}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item href="/dashboard">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Business Entity</Breadcrumb.Item>
          <Breadcrumb.Item>Groups</Breadcrumb.Item>
        </Breadcrumb>
        <br />
        <div className="border-container">
          <Button
            type="primary"
            variant="dashed"
            className="card-button"
            size="large"
            icon={<PlusOutlined />}
            onClick={showChildDrawer}
            shape="circle"
          />
          <Card title="Groups" bordered>
          <div
              style={{
                maxWidth: '100%', // Ensure the container doesn't exceed its width
                overflowX: 'auto', // Enable horizontal scroll
              }}
            >
            <Table size="middle" locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No records found!'/> }} components={{
                header: {
                  cell: ResizableTitle, // RESIZE CHANGE: Apply resizable headers
                },
              }}
              columns={mergedColumns} dataSource={allGroupsForDisplay} />
              </div>
          </Card>
        </div>
        {/* Create component with ref */}
        <Create ref={createRef} onRefresh={refreshTable} />
        <Edit ref={editRef} onRefresh={refreshTable} />
        <View ref={viewRef} onRefresh={refreshTable} />
      </Col>
    </Row>
  );
}
